import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Plus, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { notarialDocumentTypeTranslationKey } from "@/lib/notarialDocumentTypeLabel";
import {
  REQUESTABLE_DOCUMENT_TYPES,
  type DocumentRequestPayloadItem,
  type RequestableDocumentType,
} from "@/services/notaryRequestsService";

/** A suggested document from the RFQ intake, pre-selected for review. */
export interface SuggestedDocument {
  label: string;
  documentType: string | null;
  why: string;
}

export interface DocumentRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** From the parent RFQ's intake, when this request came from one. */
  suggestions?: SuggestedDocument[];
  onSubmit: (documents: DocumentRequestPayloadItem[]) => void;
  isSubmitting?: boolean;
}

interface CustomRow {
  key: string;
  label: string;
  why: string;
}

/**
 * Picking the documents to ask a consumer for (spec request-document-slots).
 *
 * The professional's only previous option was prose — "necesito el estatuto y
 * la cédula de los tres socios" — which left the consumer to translate a
 * sentence into uploads and left neither side able to say what was still
 * outstanding. Here each document is a row, so the consumer gets a slot per
 * row and both sides can see what is missing.
 *
 * Typed first, custom behind it: a typed row carries the platform's existing
 * translated label and can be matched against what the consumer already
 * uploaded, and a custom row can do neither. `notarial_certificate` is absent
 * from the picker by construction — it is the professional's OWN deliverable
 * and the consumer physically cannot upload it, so a slot asking for it could
 * never be filled.
 */
export function DocumentRequestDialog({
  open,
  onOpenChange,
  suggestions = [],
  onSubmit,
  isSubmitting = false,
}: DocumentRequestDialogProps): JSX.Element {
  const { t } = useTranslation();

  // Only suggestions the consumer could actually upload. The intake types
  // documentType over the WHOLE enum, so a prediction can legitimately name
  // the professional's own deliverable.
  const usableSuggestions = useMemo(
    () =>
      suggestions.filter(
        (s) =>
          s.documentType != null &&
          (REQUESTABLE_DOCUMENT_TYPES as readonly string[]).includes(
            s.documentType,
          ),
      ),
    [suggestions],
  );

  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(usableSuggestions.map((s) => s.documentType as string)),
  );
  const [whyByType, setWhyByType] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      usableSuggestions.map((s) => [s.documentType as string, s.why]),
    ),
  );
  const [customRows, setCustomRows] = useState<CustomRow[]>([]);

  const toggle = (type: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const filledCustom = customRows.filter((r) => r.label.trim().length >= 3);
  const total = selected.size + filledCustom.length;

  const handleSubmit = (): void => {
    const documents: DocumentRequestPayloadItem[] = [
      ...[...selected].map((type) => ({
        documentType: type as RequestableDocumentType,
        why: whyByType[type]?.trim() || undefined,
      })),
      ...filledCustom.map((r) => ({
        customLabel: r.label.trim(),
        why: r.why.trim() || undefined,
      })),
    ];
    if (documents.length === 0) return;
    onSubmit(documents);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t("notary.requestDetail.documentRequestTitle")}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          {t("notary.requestDetail.documentRequestHint")}
        </p>

        {usableSuggestions.length > 0 ? (
          // Pre-selected, never pre-sent. The intake frames these as optional
          // suggestions to help price the job; turning one into a formal
          // request changes its standing, and that is the professional's call.
          <p className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-muted-foreground">
            {t("notary.requestDetail.documentRequestSuggested", {
              count: usableSuggestions.length,
            })}
          </p>
        ) : null}

        <div className="space-y-2">
          {REQUESTABLE_DOCUMENT_TYPES.map((type) => {
            const isOn = selected.has(type);
            return (
              <div key={type} className="rounded-md border border-border p-3">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id={`doc-${type}`}
                    checked={isOn}
                    onCheckedChange={() => toggle(type)}
                    disabled={isSubmitting}
                  />
                  <Label htmlFor={`doc-${type}`} className="flex-1 font-normal">
                    {t(notarialDocumentTypeTranslationKey(type, "notary"), {
                      defaultValue: type,
                    })}
                  </Label>
                </div>
                {/* The reason only matters for something being asked for. */}
                {isOn ? (
                  <Input
                    className="mt-2 ml-7 w-[calc(100%-1.75rem)]"
                    value={whyByType[type] ?? ""}
                    onChange={(e) =>
                      setWhyByType((prev) => ({
                        ...prev,
                        [type]: e.target.value,
                      }))
                    }
                    placeholder={t(
                      "notary.requestDetail.documentRequestWhyPlaceholder",
                    )}
                    disabled={isSubmitting}
                  />
                ) : null}
              </div>
            );
          })}
        </div>

        <div className="space-y-2">
          {customRows.map((row) => (
            <div key={row.key} className="flex items-start gap-2">
              <div className="flex-1 space-y-2">
                <Input
                  value={row.label}
                  onChange={(e) =>
                    setCustomRows((prev) =>
                      prev.map((r) =>
                        r.key === row.key ? { ...r, label: e.target.value } : r,
                      ),
                    )
                  }
                  placeholder={t(
                    "notary.requestDetail.documentRequestCustomPlaceholder",
                  )}
                  disabled={isSubmitting}
                />
                <Input
                  value={row.why}
                  onChange={(e) =>
                    setCustomRows((prev) =>
                      prev.map((r) =>
                        r.key === row.key ? { ...r, why: e.target.value } : r,
                      ),
                    )
                  }
                  placeholder={t(
                    "notary.requestDetail.documentRequestWhyPlaceholder",
                  )}
                  disabled={isSubmitting}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-destructive"
                onClick={() =>
                  setCustomRows((prev) => prev.filter((r) => r.key !== row.key))
                }
                disabled={isSubmitting}
                aria-label={t(
                  "notary.requestDetail.documentRequestRemoveCustom",
                )}
              >
                <X />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              setCustomRows((prev) => [
                ...prev,
                { key: crypto.randomUUID(), label: "", why: "" },
              ])
            }
            disabled={isSubmitting}
          >
            <Plus className="h-4 w-4" />
            {t("notary.requestDetail.documentRequestAddCustom")}
          </Button>
        </div>

        <DialogFooter className="flex-wrap items-center gap-2">
          <p className="mr-auto text-xs text-muted-foreground">
            {total === 0
              ? t("notary.requestDetail.documentRequestNoneSelected")
              : t("notary.requestDetail.documentRequestCount", {
                  count: total,
                })}
          </p>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={total === 0 || isSubmitting}>
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {t("notary.requestDetail.documentRequestSubmit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
